import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { WallFacadeTable } from "building-types/facades/facadeTypes";
import { FacadeTrimBehavior } from "building-types/facades/facadeTypesEnum";
import type { Label } from "building-types/facades/label";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type {
  CornerBoardConfig,
  CornerCondition,
  CornerBoardBehavior,
  CornerBoardRail,
  CornerBoards,
  CornerBoardProfileOptions,
  CornerBoardRefPointOptions,
} from "building-types/trim/cornerBoardTypes";
import { ValidCornerCondition, InvalidCornerCondition } from "building-types/trim/cornerBoardTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb2Grow } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import { tagFaces } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import { clipFaces, clippingNodeNew } from "geom/bspClipping";
import { DIST_EPSILON, Line2Side } from "geom/core";
import { extrudePolyLine2, extrusionToBRep } from "geom/extrude";
import { line2Normal, line2Side } from "geom/line2";
import { offsetPolyLine2Variable, offsetPolyLine2 } from "geom/offset";
import { planeNewXY } from "geom/plane";
import { planeBSPByPointAndNormal } from "geom/planeBSP";
import {
  polyLine2EdgeEnd,
  polyLine2Copy,
  polyLine2ScaleY,
  polyLine2Rotate,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2Normalize, vec2AngleBetween } from "geom/vec2";
import { iterateFacadesForHalfEdge } from "../../utils/facade";

export const ROOF_CORNER_BOARD_TOLERANCE = 1;
export const CORNER_DETECTION_TOLERANCE = 1e-4;
export const CORNER_BOARD_COLLISION_TOLERANCE = 0.1;

export function cornerBoardsNew(
  id: string,
  levelId: string,
  faces: FaceArrayViewEntity[],
  rails: CornerBoardRail[],
): CornerBoards {
  return {
    ...buildingEntityBaseNew("CORNER_BOARDS", id),
    levelId,
    faces,
    rails,
  };
}

export function cornerBoardConfigForEdge(
  facades: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge1: number,
  edge2: number,
): CornerBoardConfig {
  return {
    outsideBehavior: {
      edgeBehavior1: cornerBoardBehaviorForEdge(facades, labels, edge1, false),
      edgeBehavior2: cornerBoardBehaviorForEdge(facades, labels, edge2, false),
    },
    insideBehavior: {
      edgeBehavior1: cornerBoardBehaviorForEdge(facades, labels, edge1, true),
      edgeBehavior2: cornerBoardBehaviorForEdge(facades, labels, edge2, true),
    },
  };
}

export function emptyCornerBoardConfig(): CornerBoardConfig {
  return {
    outsideBehavior: {},
    insideBehavior: {},
  };
}

function cornerBoardBehaviorForEdge(
  wallFacadeTable: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge: number,
  isInsideCorner: boolean,
): FacadeTrimBehavior | undefined {
  let result: FacadeTrimBehavior | undefined = undefined;

  iterateFacadesForHalfEdge(wallFacadeTable, labels, edge, (facade) => {
    if (facade) {
      result = isInsideCorner ? facade.insideCornerBoardBehavior ?? undefined : facade.cornerBoardBehavior ?? undefined;
      return true;
    }
    return false;
  });

  return result;
}

export function getCornerCondition(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): CornerCondition {
  const dir = line2Side(ax, ay, bx, by, cx, cy, CORNER_DETECTION_TOLERANCE);

  switch (dir) {
    case Line2Side.LEFT:
      return ValidCornerCondition.INSIDE;
    case Line2Side.RIGHT:
      return ValidCornerCondition.OUTSIDE;
    default:
      return InvalidCornerCondition.COLLINEAR;
  }
}

export function isValidCornerCondition(cornerCondition: CornerCondition): cornerCondition is ValidCornerCondition {
  return cornerCondition !== InvalidCornerCondition.COLLINEAR;
}

function mergeCornerBoardBehavior(
  behaviorA: CornerBoardBehavior,
  behaviorB?: CornerBoardBehavior,
): CornerBoardBehavior {
  return {
    edgeBehavior1: behaviorA.edgeBehavior1 ?? behaviorB?.edgeBehavior1,
    edgeBehavior2: behaviorA.edgeBehavior2 ?? behaviorB?.edgeBehavior2,
  };
}

export function mergeCornerBoardConfig(configA: CornerBoardConfig, configB?: CornerBoardConfig): CornerBoardConfig {
  return {
    outsideBehavior: mergeCornerBoardBehavior(configA.outsideBehavior, configB?.outsideBehavior),
    insideBehavior: mergeCornerBoardBehavior(configA.insideBehavior, configB?.insideBehavior),
  };
}

interface ResolveCornerBoardsOptions {
  readonly cornerCondition: ValidCornerCondition;
  readonly enableOutsideCornerMoulding: boolean;
  readonly enableInsideCornerMoulding: boolean;
  readonly config: CornerBoardConfig;
}

interface ResolvedCornerBoards {
  readonly hasBoardEdge1: boolean;
  readonly hasBoardEdge2: boolean;
}

export function resolveCornerBoards({
  cornerCondition,
  enableOutsideCornerMoulding,
  enableInsideCornerMoulding,
  config,
}: ResolveCornerBoardsOptions): ResolvedCornerBoards {
  const resolveCornerBoardBehavior = (behavior: CornerBoardBehavior, enableCornerMoulding): ResolvedCornerBoards => {
    const { edgeBehavior1, edgeBehavior2 } = behavior;

    const forceBoard1 = edgeBehavior1 === FacadeTrimBehavior.ADD;
    const forceBoard2 = edgeBehavior2 === FacadeTrimBehavior.ADD;

    const hasBoardEdge1 = forceBoard1 || (edgeBehavior1 !== FacadeTrimBehavior.REMOVE && enableCornerMoulding);
    const hasBoardEdge2 = forceBoard2 || (edgeBehavior2 !== FacadeTrimBehavior.REMOVE && enableCornerMoulding);

    return { hasBoardEdge1, hasBoardEdge2 };
  };

  switch (cornerCondition) {
    case ValidCornerCondition.OUTSIDE: {
      return resolveCornerBoardBehavior(config.outsideBehavior, enableOutsideCornerMoulding);
    }
    case ValidCornerCondition.INSIDE: {
      return resolveCornerBoardBehavior(config.insideBehavior, enableInsideCornerMoulding);
    }
  }
}

export function buildCornerBoardProfile(options: CornerBoardProfileOptions) {
  const {
    ax,
    ay,
    bx,
    by,
    cx,
    cy,
    thickness,
    wallOffsetEdge1,
    wallOffsetEdge2,
    startZ,
    endZ,
    faceArrays,
    cornerBoardRails,
    hasBoardEdge1,
    hasBoardEdge2,
    material,
    viewEntityDescriptor,
    profileId,
    cornerCondition,
    entityHandleForSelectingReportingUnit,
    pickHandle,
    streetOrientationEdge1,
    streetOrientationEdge2,
  } = options;

  const dist = endZ - startZ;

  if (dist < DIST_EPSILON) {
    return;
  }

  const extrusionPlane = planeNewXY(0, 0, startZ);

  const tag: BuildingGeomTag = {
    type: "CONDITION",
    condition: "CORNER_BOARD_PROFILE",
  };

  const extrudeProfile = (profile: PolyLine2, isProfile1: boolean) => {
    if (!profile) {
      return;
    }

    const extrusion = extrudePolyLine2(profile, extrusionPlane, 0, 0, dist);
    let faces = extrusionToBRep(extrusion).faces;

    if (hasBoardEdge1 && hasBoardEdge2) {
      const cornerPl = offsetPolyLine2Variable(polyLine2New([ax, ay, bx, by, cx, cy]), [
        -wallOffsetEdge1,
        -wallOffsetEdge2,
      ]);
      const outsideCornerPl = offsetPolyLine2(cornerPl, -thickness);
      const [cornerX, cornerY] = polyLine2EdgeEnd(cornerPl, 0);
      const [outsideCornerX, outsideCornerY] = polyLine2EdgeEnd(outsideCornerPl, 0);

      const [nX, nY] = line2Normal(cornerX, cornerY, outsideCornerX, outsideCornerY);

      const planeBSP = planeBSPByPointAndNormal(outsideCornerX, outsideCornerY, 0, nX, nY, 0);
      const bsp = addConvexPlanesToBSPLeaf([planeBSP]);
      faces = clipFaces({
        faces,
        options: {
          clippingNodes: [clippingNodeNew(bsp, isProfile1)],
        },
      });
    }

    faceArrays.push(
      faceArrayViewEntityNew(
        tagFaces(faces, tag),
        viewEntityDescriptor,
        material,
        pickHandle,
        undefined,
        undefined,
        entityHandleForSelectingReportingUnit,
      ),
    );
    const rail = new Float64Array([bx, by, startZ, bx, by, endZ]);
    const streetOrientation = isProfile1 ? streetOrientationEdge1 : streetOrientationEdge2;
    cornerBoardRails.push({ rail, profileId, cornerCondition, streetOrientation });
  };

  const { profileEdge1, profileEdge2 } = buildCornerBoardProfiles(options);

  if (profileEdge1) {
    extrudeProfile(profileEdge1, true);
  }
  if (profileEdge2) {
    extrudeProfile(profileEdge2, false);
  }
}

interface CornerBoardProfiles {
  profileEdge1?: PolyLine2;
  profileEdge2?: PolyLine2;
}

function buildCornerBoardProfiles({
  ax,
  ay,
  bx,
  by,
  cx,
  cy,
  wallOffsetEdge1,
  wallOffsetEdge2,
  hasBoardEdge1,
  hasBoardEdge2,
  profile,
  width,
  thickness,
  cornerCondition,
}: CornerBoardProfileOptions): CornerBoardProfiles {
  let profileEdge1: PolyLine2 | undefined;
  let profileEdge2: PolyLine2 | undefined;

  const cornerPl = offsetPolyLine2Variable(polyLine2New([ax, ay, bx, by, cx, cy]), [
    -wallOffsetEdge1,
    -wallOffsetEdge2,
  ]);
  const outsideCornerPl = offsetPolyLine2(cornerPl, -thickness);
  const [cornerX, cornerY] = polyLine2EdgeEnd(cornerPl, 0);
  const [outsideCornerX, outsideCornerY] = polyLine2EdgeEnd(outsideCornerPl, 0);
  const isInsideCorner = cornerCondition === ValidCornerCondition.INSIDE;

  if (hasBoardEdge1) {
    const [tx, ty] = vec2Normalize(ax - bx, ay - by);
    const rads = vec2AngleBetween(tx, ty, 0, 1);

    const x = isInsideCorner || !hasBoardEdge2 ? cornerX + ty * thickness : outsideCornerX;
    const y = isInsideCorner || !hasBoardEdge2 ? cornerY - tx * thickness : outsideCornerY;

    profileEdge1 = polyLine2Copy(profile);

    if (!isInsideCorner && !hasBoardEdge2) {
      polyLine2ScaleY(profileEdge1, (width - thickness) / width);
    }

    polyLine2Rotate(profileEdge1, -rads);
    polyLine2Move(profileEdge1, x, y);
  }

  if (hasBoardEdge2) {
    const [tx, ty] = vec2Normalize(cx - bx, cy - by);
    const rads = vec2AngleBetween(tx, ty, 0, 1);

    const x = isInsideCorner || !hasBoardEdge1 ? cornerX : outsideCornerX + ty * thickness;
    const y = isInsideCorner || !hasBoardEdge1 ? cornerY : outsideCornerY - tx * thickness;

    profileEdge2 = polyLine2Copy(profile);
    polyLine2ReflectOverYAxis(profileEdge2);
    polyLine2Move(profileEdge2, -thickness, 0);
    polyLine2Reverse(profileEdge2);

    if (!isInsideCorner && !hasBoardEdge1) {
      polyLine2ScaleY(profileEdge2, (width - thickness) / width);
    }

    polyLine2Rotate(profileEdge2, -rads);
    polyLine2Move(profileEdge2, x, y);
  }

  return { profileEdge1, profileEdge2 };
}

export function getCornerBoardRefPointFromOffsets(
  { ax, ay, bx, by, cx, cy }: CornerBoardRefPointOptions,
  offsetEdge1: number,
  offsetEdge2: number,
) {
  const cornerPl = offsetPolyLine2Variable(polyLine2New([ax, ay, bx, by, cx, cy]), [offsetEdge1, offsetEdge2]);

  return polyLine2EdgeEnd(cornerPl, 0);
}

export function getCornerBoardRefPoint(options: CornerBoardRefPointOptions) {
  return getCornerBoardRefPointFromOffsets(options, -options.wallOffsetEdge1, -options.wallOffsetEdge2);
}

export function getCornerBoardRefAABB(options: CornerBoardRefPointOptions) {
  const [cornerX, cornerY] = getCornerBoardRefPoint(options);

  return aabb2Grow(
    aabb2New(cornerX, cornerY, cornerX, cornerY),
    CORNER_BOARD_COLLISION_TOLERANCE,
    CORNER_BOARD_COLLISION_TOLERANCE,
  );
}
